import React, { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import Sidebar from "./navigation/Sidebar";
import ShellBreadcrumb from "./navigation/ShellBreadcrumb";
import config from "../assets/utils/config";
import { useAuth } from "../assets/utils/useAuth";
import { initializeSidebar } from "../stores/sidebar";
import CopyRight from "./compoinents/copywrite";
import { GlobalSearchProvider } from "../contexts/GlobalSearchContext";
import { PageHeaderProvider } from "../contexts/PageHeaderContext";
import UniversalNotificationsProvider from "../components/Notifications/UniversalNotificationsProvider";
import useClaimsRollout from "../hooks/useClaimsRollout";
import { AdminNotifyProvider } from "./notify";
import { NotificationHealthBanner } from "./notify/NotificationSystemPage";
import { usePermissions } from "../assets/utils/usePermissions";

/** Persistent outage warning for Admin (Notification System v2 §8.4); others never query it. */
const NotificationHealthGate = () => {
  const { hasAnyPermission } = usePermissions();
  return hasAnyPermission(['admin.*', 'admin.system_config']) ? <NotificationHealthBanner /> : null;
};

/**
 * The navbar bell and dropdown read NotificationContext, whose provider was
 * never mounted — so they showed 0 unread. It is mounted once the claims
 * monthly-completion notice (spec §10) is switched on, which is when the bell
 * starts carrying something the Claims team must see. Off, nothing changes:
 * no extra polling, same tree as before.
 */
const NotificationsGate = ({ children }) => {
  const { month_complete_notice_enabled: enabled } = useClaimsRollout();
  return enabled ? <UniversalNotificationsProvider>{children}</UniversalNotificationsProvider> : children;
};


const Layout = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { collapsed } = useSelector((state) => state.sidebar || { collapsed: false });
  const isLogin = location.pathname === '/login';

  // Dark mode state - matching navigation theme system
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('sidebarDarkMode');
    return saved ? JSON.parse(saved) : false;
  });

  useEffect(() => {
    if(!user) {
      navigate('/login');
    }
  }, [user]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Initialize sidebar state from localStorage
    dispatch(initializeSidebar());
  }, [dispatch]);

  // Listen for theme changes from navigation
  useEffect(() => {
    const handleStorageChange = () => {
      const saved = localStorage.getItem('sidebarDarkMode');
      setDarkMode(saved ? JSON.parse(saved) : false);
    };

    // Listen for storage events (cross-tab)
    window.addEventListener('storage', handleStorageChange);
    
    // Listen for custom theme change events
    const handleThemeChange = () => {
      const saved = localStorage.getItem('sidebarDarkMode');
      setDarkMode(saved ? JSON.parse(saved) : false);
    };
    
    window.addEventListener('themeChanged', handleThemeChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('themeChanged', handleThemeChange);
    };
  }, []);

  // Apply dark mode classes to body and root elements
  useEffect(() => {
    const body = document.body;
    const root = document.getElementById('root');
    
    if (darkMode) {
      body.classList.add('dark-mode');
      if (root) root.classList.add('dark-mode');
    } else {
      body.classList.remove('dark-mode');
      if (root) root.classList.remove('dark-mode');
    }
  }, [darkMode]);

  window.onunload = () => {
    localStorage.setItem(config.key.q_count, 0)
    localStorage.setItem(config.key.q_reading, 0)
    localStorage.setItem(config.key.activeSeg, 0)
    localStorage.setItem(config.key.activePrifileType, 0)
  }

  const shellClass = [
    'app-shell',
    isLogin ? 'app-shell--no-sidebar' : '',
    !isLogin && collapsed ? 'app-shell--sidebar-collapsed' : '',
    darkMode ? 'dark-mode' : '',
  ].filter(Boolean).join(' ');

  return (
    <GlobalSearchProvider>
      <PageHeaderProvider>
        <AdminNotifyProvider>
        <NotificationsGate>
        <div className={shellClass}>
          {!isLogin && (<Sidebar />)}
          <audio preload="auto" src="" id="audio"></audio>

          <div className={`main-content ${!isLogin && collapsed ? 'sidebar-collapsed' : ''} ${darkMode ? 'dark-mode' : ''}`}>
            {!isLogin && <ShellBreadcrumb />}
            {!isLogin && <NotificationHealthGate />}
            <div className="content-wrapper">
              <Outlet />
            </div>
            {!isLogin && ( <CopyRight /> )}
          </div>
        </div>
        </NotificationsGate>
        </AdminNotifyProvider>
      </PageHeaderProvider>
    </GlobalSearchProvider>
  );
};

export default Layout;
